import '../windows/style.css';
import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import DashLayout from '../../layout/DashLayout';
import { usePersistentState, checkRoles } from '../../utils';
import { WindowRenderer, getMenuList } from '../windows/MenuList';
import { FullScreenContext } from '../../context/FullScreenContext';
import { icons } from '../windows/Icons';
import { useTheme } from '../../context/ThemeContext.js.js';
import { ExitFullScreen, FullScreenSvg, HomeSvg, LockSvg, NecttosLogo, NLogo, OLoading, SearchIconSvg, SettingstSvg, SpeedoMeterSvg, SupportSvg } from '../../static/svg/icons';
import { NecttosContext } from '../../context/NecttosContext';
import { motion } from 'framer-motion';
import moment from 'moment';
import SettingsPage from '../../NecttosComp/SettingsPage';

function PrimaryDashboard() {
	const { collegeId, college, isLoading, profile, role, icons: icon = [] } = useContext(NecttosContext);
	const verificationWindows = college?.verificationWindows;
	const inputRef = useRef(null);
	const [favoriteIcons, setFavoriteIcons] = usePersistentState('favIcons', {
		icon1: {},
		icon2: {},
		icon3: {},
		icon4: {},
		icon5: {},
		icon6: {},
		icon7: {},
		icon8: {},
	});
	const [currentNav, setCurrentNav] = useState('home');
	const [isHover, setIsHover] = useState(false);

	const MENU_ITEMS = useMemo(() => getMenuList(collegeId, verificationWindows, college, role), [collegeId, verificationWindows, college, role]);

	const [classification, setClassification] = useState('All');
	const { isFullScreen, togleFullScreen } = useContext(FullScreenContext);
	const { theme, toggleTheme } = useTheme();
	const [lock, setLock] = usePersistentState('lock', false);
	const [pinNo, setPinNo] = useState('');
	const [currentTime, setCurrentTime] = useState(moment().format('HH:mm'));
	const [messages, setMessages] = useState([
		{ id: 1, text: 'Hello, how can I help you today?', sender: 'support' },
		{ id: 2, text: 'I need assistance with my account settings.', sender: 'user' },
	]);

	const [criteria, setCriteria] = usePersistentState('homePage', {});
	const [windows, setWindows] = usePersistentState('homePage', {
		issueBook: false,
		renewBook: false,
		returnBook: false,
		opac: false,
		master: false,
		questionBankCreator: false,
	});

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === '/') {
				event.preventDefault();
				inputRef.current?.focus();
			}
			if (event.metaKey && event.key === 'f') {
				event.preventDefault();
				inputRef.current?.focus();
			}
			if (event.key === 'Escape') {
				event.preventDefault();
				setWindows({ issueBook: false, renewBook: false, returnBook: false, opac: false, master: false, questionBankCreator: false });
				setSearchQuery('');
			}
			if (event.altKey && (event.key === 't' || event.key === 'T')) {
				event.preventDefault();
				toggleTheme();
			}
			if (event.ctrlKey && event.key === 't') {
				event.preventDefault();
				toggleTheme();
			}
			if (event.ctrlKey && event.key === 'l') {
				event.preventDefault();
				handleLock();
			}
			if (event.ctrlKey && event.key === 'f') {
				event.preventDefault();
				togleFullScreen();
			}
			if (event.ctrlKey && event.key == '1') {
				event.preventDefault();
				setWindows({ ...windows, [favoriteIcons['icon1'].keyData]: true });
			}
			if (event.ctrlKey && event.key == '2') {
				event.preventDefault();
				setWindows({ ...windows, [favoriteIcons['icon2'].keyData]: true });
			}
			if (event.ctrlKey && event.key == '3') {
				event.preventDefault();
				setWindows({ ...windows, [favoriteIcons['icon3'].keyData]: true });
			}
			if (event.ctrlKey && event.key == '4') {
				event.preventDefault();
				setWindows({ ...windows, [favoriteIcons['icon4'].keyData]: true });
			}
			if (event.ctrlKey && event.key == '5') {
				event.preventDefault();
				setWindows({ ...windows, [favoriteIcons['icon5'].keyData]: true });
			}
			if (event.ctrlKey && event.key == '6') {
				event.preventDefault();
				setWindows({ ...windows, [favoriteIcons['icon6'].keyData]: true });
			}
			if (event.ctrlKey && event.key == '7') {
				event.preventDefault();
				setWindows({ ...windows, [favoriteIcons['icon7'].keyData]: true });
			}
			if (event.ctrlKey && event.key == '8') {
				event.preventDefault();
				setWindows({ ...windows, [favoriteIcons['icon8'].keyData]: true });
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [favoriteIcons]);

	useEffect(() => {
		const appWindow = document.querySelector('.app-window');
		if (appWindow) {
			appWindow.classList.add('open');
		}
	}, []);

	const [searchQuery, setSearchQuery] = useState('');


	const searchData = useMemo(() => {
		return MENU_ITEMS?.filter((item) => [...icon, 'featureManagement']?.includes(item?.keyData) && item.title.toLowerCase().includes(searchQuery.toLowerCase()));
	}, [searchQuery, MENU_ITEMS]);

	const ICON_BACKGROUNDS = [
		'#20A0D1', // Sky Blue
		'#00A482', // Teal
		'#0676DA', // Royal Blue
		'#6471D9', // Indigo
		'#F0635C', // Coral Red
		'#FFB347', // Soft Orange
		'#7E57C2', // Purple
		'#26A69A', // Aqua
		'#FF7043', // Orange Accent
		'#5C6BC0', // Slate Blue
	];

	const ICON_BACKGROUNDS_DARK = [
		'#104E65', // Dark Sky Blue
		'#005B4A', // Dark Teal
		'#03407F', // Deep Royal Blue
		'#3C3F80', // Deep Indigo
		'#7A312C', // Dark Coral Red
		'#B2742E', // Burnt Orange
		'#45277A', // Deep Purple
		'#145E59', // Deep Aqua
		'#A1361D', // Deep Orange
		'#39449A', // Dark Slate Blue
	];

	// Function to handle dragging an item
	const handleDragStart = (e, keyData, bgColor) => {
		const dragData = JSON.stringify({ keyData, bgColor }); // Convert data to JSON
		e.dataTransfer.setData('keyData', dragData);
	};

	// Function to handle dropping an item into a favorite slot
	const handleDrop = (e, slot) => {
		e.preventDefault();

		try {
			const data = e.dataTransfer.getData('keyData');
			if (!data) {
				console.error('No data found in dataTransfer');
				return;
			}

			const { keyData, bgColor } = JSON.parse(data); // Safely parse the JSON data
			setFavoriteIcons((prevIcons) => ({
				...prevIcons,
				[slot]: { keyData, bgColor },
			}));
		} catch (error) {
			console.error('Error parsing dataTransfer JSON:', error);
		}
	};
	// Prevent the default behavior for the dragover event
	const handleDragOver = (e) => {
		e.preventDefault();
	};

	const memoizedSearchResults = useMemo(() => {
		return searchData?.reduce((acc, item, index) => {
			const isValidRole = checkRoles(item.allowedUsers, role, collegeId);
			const isMatchingClassification = classification === 'All' ? true : item.classification === classification;
			const isNotNAAC = !['c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7'].includes(item.classification.toLowerCase());

			if ((searchQuery && isValidRole) || (isValidRole && isMatchingClassification && isNotNAAC)) {
				const bgColor = theme === 'dark' ? ICON_BACKGROUNDS_DARK[index % ICON_BACKGROUNDS_DARK.length] : ICON_BACKGROUNDS[index % ICON_BACKGROUNDS.length];
				acc.push(
					<div className=' w-[160px] float-left m-[20px] flex flex-col items-center ' key={item?.keyData + item.classification} draggable onDragStart={(e) => handleDragStart(e, item.keyData, bgColor)}>
						<div id={item?.keyData + item.classification} className=' text-center w-[120px] h-[120px] flex flex-col items-center space-y-2'>
							<div onClick={() => setWindows({ [item?.keyData]: true })} className='w-[72px] h-[72px] sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center cursor-pointer transition-transform duration-300 shadow-lg hover:scale-105 hover:z-[0]' style={{ backgroundColor: bgColor }}>
								<img src={icons[item.keyData] || item?.img} alt={item?.title} className='w-[72px] h-[72px] sm:w-12 sm:h-12 object-contain' />
							</div>
						</div>
						<div className='w-[160px] flex justify-center'>
							<h2 className='dark:text-white   text-center text-sm sm:text-base  font-sans font-semibold leading-snug break-words'>{item?.title}</h2>
						</div>
					</div>,
				);
			}
			return acc;
		}, []);
	}, [searchData, role, classification, searchQuery, collegeId]);

	const getScreen = () => {
		switch (currentNav) {
			case 'home':
				return (
					<div className='flex w-full'>
						<div className='flex flex-col w-full bg-transparent'>
							<div className='flex flex-row overflow-x-scroll self-center z-[0] fixed bg-gradient-to-b from-[#f5f5f7] via-[#f5f5f7c3] to-[#f5f5f700] dark:from-[#151515] dark:via-[#151515df] transition-opacity items-center justify-center space-x-4 px-2 py-1 w-[90vw]'>
								{['All', 'Office','Admission', 'Academics', 'Accounts', 'FYUGP', 'Library', 'DDFS', 'Administration', 'NAAC', 'IQAC'].map((role, index) => (
									<div key={index} className={`relative dark:text-white  p-1 rounded-xl cursor-pointer text-sans flex items-center justify-center transition-all duration-300 ease-in-out transform hover:scale-105`} onClick={() => setClassification(role)}>
										<span className='font-semibold'>{role}</span>
										{classification === role && <div className='absolute bottom-0 left-0 w-full h-[3px] bg-blue-500 rounded-t-lg   transition-transform duration-300 ease-in-out scale-x-100' />}
										{classification !== role && <div className='absolute bottom-0 left-0 w-full h-[3px] bg-blue-500 rounded-t-lg transition-transform duration-300 ease-in-out scale-x-0' />}
									</div>
								))}
							</div>

							<div className={`${isHover && 'ms-[-3vw]'} w-[80vw] self-center flex ml-[4vw] pb-[4vh] max-h-[92vh]`}>
								<div className='justify-center flex overflow-y-scroll w-fit'>
									<div className='grid-auto justify-start flex flex-wrap  pt-12'>{memoizedSearchResults}</div>
								</div>
							</div>
						</div>
						<div className='left-side-panel h-[94vh] w-[4vw] justify-center items-center mx-2 flex '>
							<div className='grid grid-rows-8 h-[70vh]'>
								{Object.keys(favoriteIcons).map((slot, index) => {
									const icon = MENU_ITEMS.find((x) => favoriteIcons[slot]?.keyData === x?.keyData);

									const bgColor = theme === 'dark' ? ICON_BACKGROUNDS_DARK[index % ICON_BACKGROUNDS_DARK.length] : ICON_BACKGROUNDS[index % ICON_BACKGROUNDS.length];
									return (
										<div
											key={index}
											onClick={() => setWindows({ [icon?.keyData]: true })}
											className='icon  h-[60px] w-[60px] justify-center items-center flex bg-[#1a54c8] rounded-xl cursor-pointer'
											onDrop={(e) => handleDrop(e, slot)}
											onDragOver={handleDragOver}
											onDragStart={(e) => handleDragStart(e, icon?.keyData, bgColor)}
											style={{
												backgroundColor: icon?.bgColor || bgColor,
											}}>
											{icon?.keyData && <img src={icons[icon?.keyData] || icon?.img} alt='Favorite Icon' className='w-[40px] h-[40px] object-contain' />}
										</div>
									);
								})}
							</div>
						</div>
					</div>
				);
			case 'settings':
				return <SettingsPage />;
			case 'support':
				return (
					<div className='flex flex-col h-full bg-gray-100 dark:bg-gray-800 '>
						{/* Maintenance Alert */}
						<div className='bg-yellow-400 text-black p-4 text-center font-semibold'>This feature is under maintenance. Please check back later.</div>

						<div className='flex justify-between items-center p-4 bg-gray-200 dark:bg-gray-900'>
							<span className='text-lg font-semibold text-gray-800 dark:text-white'>Support</span>
						</div>

						<div className='flex flex-col flex-grow p-4 space-y-4 overflow-y-auto '>
							{/* Chat messages */}
							<div className='flex flex-col space-y-3'>
								{messages.map((message) => (
									<div key={message.id} className={`flex items-start space-x-3 ${message.sender === 'support' ? 'justify-start' : 'justify-end'}`}>
										<div className={`${message.sender === 'support' ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-800'} p-3 rounded-lg max-w-xs`}>{message.text}</div>
									</div>
								))}
							</div>
						</div>

						<div className='p-4 bg-gray-200 dark:bg-gray-900'>
							<input
								type='text'
								placeholder='Type your message...'
								className=' dark:bg-black text-gray-800 dark:text-white p-3 w-full rounded-full border-none focus:outline-none'
								onKeyPress={(e) => {
									if (e.key === 'Enter') {
										addMessage(e.target.value, 'user');
										e.target.value = '';
									}
								}}
							/>
						</div>
					</div>
				);
		}
	};

	const addMessage = (messageText, sender) => {
		setMessages((prevMessages) => [...prevMessages, { id: prevMessages.length + 1, text: messageText, sender }]);
	};

	const handleLock = () => {
		setLock(true);
	};

	useEffect(() => {
		if (lock && profile) {
			const formattedDate = moment(profile.dob).format('DDMMYYYY');
			if (formattedDate == pinNo) {
				setPinNo('');
				setLock(false);
			}
		}
	}, [pinNo, lock]);

	useEffect(() => {
		if (lock) {
			const interval = setInterval(() => {
				setCurrentTime(moment().format('HH:mm'));
			}, 4000);

			return () => clearInterval(interval);
		}
	}, []);

	return (
		<DashLayout>
			{lock && (
				<div className='app-window className max-h-[100%] max-w-[100%] ' style={{ zIndex: 1000 }}>
					<div className={`grid relative flex-col bg-black/30 backdrop-blur-[1px] transition duration-500 autoWidthAnimation pt-4 h-[100vh] w-[100vw]`}>
						<div className='flex self-center justify-between items-center flex-col h-[75vh]'>
							<div className='time flex flex-col justify-evenly items-center'>
								<span className='text-center justify-start text-white/40 text-3xl font-semibold font-sans leading-tight'>{new Date().toDateString()}</span>
								<span className='text-center justify-start text-white/80 text-8xl font-semibold font-sans leading-tight'>{currentTime}</span>
							</div>
							<div className='lock flex flex-col justify-evenly items-center'>
								<img src={profile?.photoURL} alt='Profile' className='h-[45px] w-[45px] rounded-full object-cover shadow-lg transition-transform hover:scale-105 float-left' />
								<div className='name  text-white/90 text-2xl font-semibold font-sans leading-normal m-2'>{profile?.name}</div>
								<input
									placeholder='Enter Pin'
									className='bg-white/20 rounded-[100px] h-7 text-center border-0 text-white text-[1em] items-baseline '
									type='password'
									name='pin'
									id='pin'
									onChange={(e) => setPinNo(e.target.value)}
									onKeyPress={(e) => {
										if (e.key === 'Enter') {
											if (pinNo === moment(profile.dob).format('DDMMYYYY')) {
												setLock(false);
												setPinNo('');
											} else {
												setLock(false);
												setPinNo('');
											}
										}
									}}
									value={pinNo}
									style={{
										textAlign: 'center', // Ensures the text is horizontally centered
										paddingLeft: '0px', // Remove default padding
										paddingRight: '0px', // Remove default padding
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			)}
			{isLoading && (
				<div className='app-window className max-h-[100%] max-w-[100%] ' style={{ zIndex: 1000 }}>
					{/* <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500'></div> */}
					<OLoading styles={{ height: 50, width: 50, size: 50 }} />
				</div>
			)}
			<div className='flex'>
				<div
					className={`left-side-panel bg-[#f5f5f7] dark:bg-[#151515] h-[100vh] grid transition-all duration-300 ease-in-out dark:backdrop-blur-2xl
    ${isHover ? 'w-[10vw]' : 'w-[3vw]'} group `}
					onMouseEnter={() => setIsHover(true)}>
					<div className='w-full items-center' onMouseLeave={() => setIsHover(false)}>
						<div className='pt-3 mb-[16vh] grid '>
							<div onClick={() => (window.location.href = '/college-list')} className='mx-auto hover:cursor-pointer'>
								{isHover ? <NecttosLogo width='8vw' height='100%' theme={theme} /> : <NLogo width='2.3vw' height='100%' />}
							</div>
						</div>
						<div className='grid gap-4 cursor-pointer float-left'>
							<div className='w-full flex justify-between'>
								{currentNav === 'home' && <div className='w-1  bg-[#1462ff] rounded-tr rounded-br top-0 bottom-0' />}
								<motion.div className='icon m-auto relative flex items-center group' onClick={() => setCurrentNav('home')} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
									<HomeSvg
										aria-label='Home'
										styles={{
											size: '2.4vw',
											fill: currentNav === 'home' ? '#1462ff' : '#7d7d7d',
										}}
										className={`transition-transform duration-300 ${currentNav === 'home' ? 'scale-110' : ''}`}
									/>
									{isHover && (
										<span
											className='dark:text-white  ml-2 text-sm transition-opacity duration-300'
											style={{
												opacity: currentNav === 'home' ? 1 : 0.7,
											}}>
											Home
										</span>
									)}
								</motion.div>
							</div>
							<div className='w-full flex justify-between '>
								{currentNav === 'support' && <div className='w-1  bg-[#1462ff] rounded-tr rounded-br top-0 bottom-0' />}
								<motion.div className='icon m-auto relative flex items-center group' onClick={() => setCurrentNav('support')} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
									<SupportSvg
										aria-label='Support'
										styles={{
											size: '2.4vw',
											fill: currentNav === 'support' ? '#1462ff' : '#7d7d7d',
										}}
										className={`transition-transform duration-300 ${currentNav === 'support' ? 'scale-110' : ''}`}
									/>
									{isHover && (
										<span
											className='dark:text-white  ml-2 text-sm  transition-opacity duration-300'
											style={{
												opacity: currentNav === 'support' ? 1 : 0.7,
											}}>
											Support
										</span>
									)}
								</motion.div>
							</div>
							<div className='w-full flex justify-between '>
								{currentNav === 'settings' && <div className='w-1  bg-[#1462ff] rounded-tr rounded-br top-0 bottom-0' />}
								<motion.div className='icon m-auto relative flex items-center group' onClick={() => setCurrentNav('settings')} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
									<SettingstSvg
										aria-label='Settings'
										styles={{
											size: '2.4vw',
											fill: currentNav === 'settings' ? '#1462ff' : '#7d7d7d',
										}}
										className={`transition-transform duration-300 ${currentNav === 'settings' ? 'scale-110' : ''}`}
									/>
									{isHover && (
										<span
											className='dark:text-white  ml-2 text-sm  transition-opacity duration-300'
											style={{
												opacity: currentNav === 'settings' ? 1 : 0.7,
											}}>
											Settings
										</span>
									)}
								</motion.div>
							</div>
						</div>
					</div>
				</div>

				<div className={` w-[97vw]  h-[100vh] flex bg-[#f5f5f7] dark:bg-[#151515] items-center justify-center `}>
					<div className='flex w-full h-full flex-col '>
						<nav className='flex flex-row justify-between items-center align-center py-2 ml-3'>
							<div className='flex align-middle justify-around items-center'>
								<img src={profile?.photoURL} alt='Profile' className='h-[35px] w-[35px] rounded-full object-cover shadow-lg transition-transform hover:scale-105 float-left' />
								<span className='dark:text-white  font-sans font-normal px-2 text-lg float-left'>Welcome, {profile?.name}</span>
							</div>

							<div className='w-[30vw] bg-[#f5f5f7] dark:bg-[#212121] h-[45px] rounded-[100px] shadow-[inset_0px_0px_15.199999809265137px_0px_rgba(0,0,0,0.06)] align-middle flex flex-row px-2 justify-center border-2 dark:border-[#151515]'>
								<div className='m-auto'>
									<SearchIconSvg styles={{ height: 20, width: 20, fill: 'grey' }} aria-label='Search Icon' />
								</div>
								<input ref={inputRef} id='search' className={`font-sans w-[28vw] text-center bg-transparent focus:bg-transparent   outline-none border-none placeholder:dark:text-white  placeholder:text-opacity-50 m-0 focus:outline-none focus:ring-0 focus:border-none focus:shadow-none`} placeholder='Search' onChange={(e) => setSearchQuery(e.target.value)} value={searchQuery} />
							</div>

							<div className='grid grid-cols-3 gap-3 mr-2  justify-center items-center'>
								{/* <ToDoListSvg aria-label='To-Do List' className='w:40 lg:w-[30px] md:w-[25px] sm:w-[20px] h:40 lg:h-[30px] md:h-[25px] sm:h-[20px]' styles={{ size: 30, fill: '#7d7d7d' }} /> */}
								<div className='' onClick={() => togleFullScreen()}>
									{isFullScreen ? <ExitFullScreen aria-label='Full Screen' className='w:40 lg:w-[30px] md:w-[25px] sm:w-[20px] h:40 lg:h-[30px] md:h-[25px] sm:h-[20px]' styles={{ size: 30, fill: '#7d7d7d' }} /> : <FullScreenSvg aria-label='Full Screen' className='w:40 lg:w-[30px] md:w-[25px] sm:w-[20px] h:40 lg:h-[30px] md:h-[25px] sm:h-[20px]' styles={{ size: 30, fill: '#7d7d7d' }} />}
								</div>
								<div onClick={handleLock} className=''>
									<LockSvg aria-label='Lock' className='w:40 lg:w-[30px] md:w-[25px] sm:w-[20px] h:40 lg:h-[30px] md:h-[25px] sm:h-[20px]' styles={{ size: 30, fill: '#7d7d7d' }} />
								</div>
								{/* <div onClick={() => setWindows({ netSpeedTest: true })}>
									<SpeedoMeterSvg aria-label='Speedometer' className='w:40 lg:w-[30px] md:w-[25px] sm:w-[20px] h:40 lg:h-[30px] md:h-[25px] sm:h-[20px]' styles={{ size: 30, fill: '#7d7d7d' }} />
								</div> */}
								<img src={college?.logo} alt='Profile' className='w:40 lg:w-[35px] md:w-[25px] sm:w-[20px] h:40 lg:h-[35px] md:h-[25px] sm:h-[20px] rounded-full object-cover shadow-lg transition-transform hover:scale-105' />
							</div>
						</nav>
						{getScreen()}
					</div>
					<WindowRenderer windows={windows} setWindows={setWindows} criteria={criteria} setCriteria={setCriteria} verificationWindows={verificationWindows} />
				</div>
			</div>
		</DashLayout>
	);
}

export default PrimaryDashboard;
